import { useCallback, useRef, useState } from 'react'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import { loadMachineIdsByProductIds } from '../../lib/productMachines'
import {
  moveSlotSameMachine,
  refreshInProgressReservation,
  transferSlotToMachine,
} from '../../lib/scheduleOperations'
import { formatQty, ganttBarStyle, SCHEDULE_SLOT_STATUS_LABELS } from '../../lib/scheduleCalculations'
import type { MachineRecord, ScheduleSlotRecord } from '../../types/records'

const SNAP_MINUTES = 30

function snapMs(ms: number): number {
  const step = SNAP_MINUTES * 60 * 1000
  return Math.round(ms / step) * step
}

export type GanttDraggableSlotProps = {
  slot: ScheduleSlotRecord
  machine: MachineRecord
  machines: MachineRecord[]
  rangeStart: Date
  hoursInDay: number
  color: string
  productName: string
  canDrag: boolean
  userId?: string
  onEdit: () => void
  onMoved: (message: string) => void
  onError: (msg: string) => void
}

export function GanttDraggableSlot({
  slot,
  machine,
  machines,
  rangeStart,
  hoursInDay,
  color,
  productName,
  canDrag,
  userId,
  onEdit,
  onMoved,
  onError,
}: GanttDraggableSlotProps) {
  const barRef = useRef<HTMLButtonElement>(null)
  const [dragging, setDragging] = useState(false)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [hoverMachineId, setHoverMachineId] = useState<string | null>(null)

  const dragRef = useRef<{
    startX: number
    startY: number
    originMachineId: string
    durationMs: number
    moved: boolean
  } | null>(null)

  const baseStyle = ganttBarStyle(slot.plannedStart, slot.plannedEnd, rangeStart, hoursInDay)
  if (!baseStyle) return null

  const draggable = canDrag && (slot.status === 'planned' || slot.status === 'in_progress')

  const finishDrag = useCallback(
    async (clientX: number, clientY: number) => {
      const drag = dragRef.current
      dragRef.current = null
      setDragging(false)
      setTranslate({ x: 0, y: 0 })
      setHoverMachineId(null)

      if (!drag || !drag.moved) return

      const dropEl = document.elementFromPoint(clientX, clientY)
      const rowEl = dropEl?.closest('[data-gantt-machine-id]') as HTMLElement | null
      const dropMachineId = rowEl?.dataset.ganttMachineId ?? drag.originMachineId

      const sourceTrack = barRef.current?.parentElement
      if (!sourceTrack) return
      const sourceRect = sourceTrack.getBoundingClientRect()
      const dayMs = hoursInDay * 3600000
      const horizontalDeltaMs = ((clientX - drag.startX) / sourceRect.width) * dayMs

      const oldStart = new Date(slot.plannedStart).getTime()
      const newStartMs = snapMs(oldStart + horizontalDeltaMs)
      const newEndMs = newStartMs + drag.durationMs

      const inProgress = slot.status === 'in_progress'
      if (inProgress) {
        const ok = window.confirm(
          'Слот в работе — материалы зарезервированы. Перенос пересчитает резерв. Продолжить?',
        )
        if (!ok) return
      }

      try {
        const productId =
          typeof slot.expand?.requestLine?.product === 'string'
            ? slot.expand.requestLine.product
            : slot.expand?.requestLine?.expand?.product?.id

        if (dropMachineId !== drag.originMachineId) {
          if (!productId) {
            onError('Не удалось определить продукцию для проверки совместимости станков.')
            return
          }
          const compatMap = await loadMachineIdsByProductIds([productId])
          const allowed = compatMap.get(productId) ?? []
          if (!allowed.includes(dropMachineId)) {
            onError('Этот станок не подходит для данной продукции.')
            return
          }
          const targetMachine = machines.find((m) => m.id === dropMachineId)
          if (!targetMachine) {
            onError('Станок не найден.')
            return
          }
          const product = slot.expand?.requestLine?.expand?.product ?? null
          const startIso = new Date(newStartMs).toISOString()
          const { updated } = await transferSlotToMachine(slot.id, {
            targetMachineId: dropMachineId,
            targetMachine,
            product,
            newStartIso: startIso,
            compressSourceQueue: true,
            createdBy: userId,
          })
          if (inProgress && productId) {
            await refreshInProgressReservation({ ...slot, machine: dropMachineId }, productId, userId)
          }
          onMoved(`Перенос на другой станок (${updated} сл.)`)
          return
        }

        const startIso = new Date(newStartMs).toISOString()
        const endIso = new Date(newEndMs).toISOString()
        const { updated } = await moveSlotSameMachine(slot.id, startIso, endIso)
        if (inProgress && productId) {
          await refreshInProgressReservation(
            { ...slot, plannedStart: startIso, plannedEnd: endIso },
            productId,
            userId,
          )
        }
        onMoved(`Смещено слотов: ${updated}`)
      } catch (e: unknown) {
        onError(pocketbaseErrorMessage(e, 'Ошибка переноса слота'))
      }
    },
    [hoursInDay, slot, machines, userId, onMoved, onError],
  )

  const onPointerDown = (e: React.PointerEvent) => {
    if (!draggable || e.button !== 0) return
    e.preventDefault()
    const durationMs = new Date(slot.plannedEnd).getTime() - new Date(slot.plannedStart).getTime()
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originMachineId: machine.id,
      durationMs,
      moved: false,
    }
    barRef.current?.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return

    drag.moved = true
    if (!dragging) setDragging(true)
    setTranslate({ x: dx, y: dy })

    const rowEl = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-gantt-machine-id]') as
      | HTMLElement
      | null
    setHoverMachineId(rowEl?.dataset.ganttMachineId ?? drag.originMachineId)
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    barRef.current?.releasePointerCapture(e.pointerId)
    if (!dragRef.current.moved) {
      dragRef.current = null
      onEdit()
      return
    }
    void finishDrag(e.clientX, e.clientY)
  }

  const title = `${productName} · ${SCHEDULE_SLOT_STATUS_LABELS[slot.status]} · ${formatQty(slot.plannedQuantity)}${
    draggable ? ' · перетащите для переноса' : ''
  }`

  return (
    <button
      ref={barRef}
      type="button"
      className={`absolute top-1.5 z-20 h-[calc(100%-12px)] overflow-hidden rounded px-1 text-left text-[0.65rem] font-medium text-white shadow-sm transition hover:brightness-110 ${
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${dragging ? 'z-50 opacity-90 ring-2 ring-indigo-300' : ''}`}
      style={{
        ...baseStyle,
        backgroundColor: color,
        minWidth: 6,
        transform: dragging ? `translate(${translate.x}px, ${translate.y}px)` : undefined,
      }}
      title={title}
      data-gantt-hover-target={hoverMachineId === machine.id && dragging ? '1' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <span className="block truncate">{productName}</span>
    </button>
  )
}
